import { Box, Input, Link, Portal } from "@chakra-ui/react";
import { BaseStack, baseColors, baseRadius, colors, customEvent, Field, Spacing } from "goi_common";
import { useCallback, useEffect, useState } from "react";

const STATUS_ITEMS = ["미리 준비하려고 해요", "임종을 앞두거나 임종하신 상태입니다."];

import { useSubmitLead } from "@/hooks/useSubmitLead";
import { questionTemplate } from "@/containers/HomeContainer/remotes/homeQuestionTemplates";
import { mubinsoLeadStatus } from "@/containers/MatchContainer/components/MatchRequest/Mubinso/mubinsoConstants";
import { formatPhoneNumber } from "@/utils/formatIdentifyNumber";
import { useGetServerTime } from "@/hooks/useGetServerTime";

/**
 * 비용비교 견적 요청 모달. 홈질문요청(postLeadV2, parlorCompare 템플릿)으로 제출.
 * 기존 ReserveHelpModal(화장 예약 도움)과 동일한 홈질문 모달 디자인을 재사용.
 */
interface CompareRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  parlorName: string;
  regionLabel: string;
}

export default function CompareRequestModal({
  isOpen,
  onClose,
  parlorName,
  regionLabel,
}: CompareRequestModalProps) {
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const { data: serverTime } = useGetServerTime();
  const { submitLead } = useSubmitLead();
  const isNight = serverTime?.is_night ?? false;

  const isPhoneValid = phone.replace(/-/g, "").length === 11;
  const canSubmit = isPhoneValid && !!status;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || loading) return;

    setLoading(true);
    try {
      await submitLead({
        phone,
        lead_type: "home",
        slack_message: questionTemplate.parlorCompare(parlorName, regionLabel),
        status: mubinsoLeadStatus(status),
        accept_night_call: isNight ? true : undefined,
      });
      // 제출 성공 시점에 발화 (mbn_card-submit과 동일 규칙 - 실패/중복은 미카운트)
      customEvent({ newGtm: { path: "parlor_detail", trigger_and_target: "cmp_card-submit" } });
      alert("요청이 완료되었습니다. 담당 지도사가 이 지역 장례식장 비교 견적을 보내드리겠습니다.");
      setPhone("");
      setStatus("");
      onClose();
    } catch (error) {
      console.error("리드 제출 실패:", error);
      alert("잠시 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  }, [canSubmit, loading, phone, status, parlorName, regionLabel, onClose, isNight, submitLead]);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Portal>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={1400}
        bg="rgba(0,0,0,0.55)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        padding="16px"
        onClick={onClose}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          position="relative"
          width="100%"
          maxWidth="320px"
          bg={baseColors.background_normal}
          borderRadius={`${baseRadius.rad_12}px`}
          padding="28px 24px 24px"
          css={{ boxShadow: "0 12px 32px rgba(0,0,0,0.18)" }}
        >
          <Box
            as="button"
            type="button"
            onClick={onClose}
            position="absolute"
            top="12px"
            right="12px"
            width="32px"
            height="32px"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            border="none"
            bg="transparent"
            color={baseColors.label_assistive_1}
            cursor="pointer"
            borderRadius="50%"
            aria-label="닫기"
            css={{ appearance: "none", "&:hover": { background: baseColors.fill_normal } }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Box>

          <Box
            as="h3"
            margin="0"
            fontSize="20px"
            fontWeight={700}
            color={baseColors.label_normal}
            letterSpacing="-0.02em"
            lineHeight="1.4"
          >
            이 지역 장례식장,
            <br />
            비교 견적 보내드릴게요
          </Box>
          <Spacing gap={8} />
          <Box fontSize="15px" lineHeight="1.6" color={baseColors.label_alternative_1}>
            연락처를 남겨주시면 인근 장례식장의 비용·시설 정보를 비교해 보내드립니다.
          </Box>
          <Spacing gap={24} />

          <BaseStack spacing={8}>
            <Box fontSize="14px" fontWeight={600} color={baseColors.label_normal}>
              연락처
            </Box>
            <Input
              placeholder="010-****-****"
              value={phone}
              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
              maxLength={13}
              size="md"
              fontSize="15px"
              inputMode="numeric"
              borderColor={baseColors.line_normal}
              _hover={{ borderColor: baseColors.line_normal }}
              _focus={{
                borderColor: baseColors.label_normal,
                boxShadow: `0 0 0 1px ${baseColors.label_normal}`,
              }}
              _placeholder={{ fontSize: "14px", color: baseColors.label_assistive_1 }}
            />
          </BaseStack>

          <Spacing gap={20} />

          <Box fontSize="14px" fontWeight={600} color={baseColors.label_normal}>
            상황을 알려주세요 (맞춤형 안내)
          </Box>
          <Spacing gap={10} />
          <Field.Radio
            title=""
            items={STATUS_ITEMS}
            value={status}
            onChange={(e: string) => {
              customEvent({
                newGtm: { path: "parlor_detail", trigger_and_target: "cmp_card-status", text: e },
              });
              setStatus(e);
            }}
            radioWidth="100%"
            className="body_14_r"
            flexDirection="column"
            alignItem="center"
          />

          <Spacing gap={22} />

          <Box
            as="button"
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            display="inline-flex"
            width="100%"
            alignItems="center"
            justifyContent="center"
            height="54px"
            bg={colors.orange650}
            color={baseColors.white}
            border="none"
            borderRadius={`${baseRadius.rad_12}px`}
            fontSize="16.5px"
            fontWeight={600}
            cursor="pointer"
            opacity={canSubmit && !loading ? 1 : 0.5}
            css={{
              appearance: "none",
              "&:hover:not(:disabled)": { background: colors.orange700 },
              "&:active:not(:disabled)": { transform: "scale(0.98)" },
            }}
          >
            {loading ? "제출 중..." : "동의하고, 비교 견적 받기"}
          </Box>

          <Spacing gap={10} />
          <Box
            fontSize="13px"
            lineHeight="1.5"
            color={baseColors.label_assistive_1}
            textAlign="center"
          >
            필수 개인정보 수집, 이용, 제공 동의{" "}
            <Link
              href="https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d"
              isExternal
              color={baseColors.label_assistive_1}
              textDecoration="underline"
            >
              보기
            </Link>
          </Box>
          {isNight && (
            <>
              <Spacing gap={4} />
              <Box
                fontSize="13px"
                lineHeight="1.5"
                color={baseColors.label_assistive_1}
                textAlign="center"
              >
                필수 긴급 상황 시 수신 동의(야간){" "}
                <Link
                  href="https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4"
                  isExternal
                  color={baseColors.label_assistive_1}
                  textDecoration="underline"
                >
                  보기
                </Link>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Portal>
  );
}
